/**
 * PPA Fair Value — two sources, auto-selected by the backend:
 *
 *  • "workbook" — the EY embedded-derivative workbook uploaded for the
 *    portfolio. The platform replicates the auditor's calc exactly: the
 *    measurement-date valuations (Day 1 → FYE), the goal-seeked day-1
 *    calibration factor (re-solved independently as a check), and a
 *    revaluation under factor / discount-rate overrides. EY conventions are
 *    preserved verbatim — single day-1 discount anchor, no quarter
 *    roll-off, so FV moves only when the Aurora curve vintage moves.
 *
 *  • "model" — mark-to-model estimate from the Aurora curve inside the
 *    active RevFM lender model (used when no EY workbook is uploaded).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, Alert, Chip,
  CircularProgress, TextField, Select, MenuItem, FormControlLabel,
  Switch, Collapse, Button, Table, TableHead, TableBody, TableRow,
  TableCell, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';
import dayjs from 'dayjs';
import PpaWorkspaceTabs from './PpaWorkspaceTabs';
import PeriodPicker from './PeriodPicker';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const GREEN = '#1f9d57';
const RED   = '#e5384f';
const GREY  = '#334155';

const fmtAud = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '';
  if (a >= 1e9) return `${s}A$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}A$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}A$${(a / 1e3).toFixed(0)}k`;
  return `${s}A$${a.toFixed(0)}`;
};
const fmtPrice = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtMwh = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-AU'));
const qLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(2)}`;
};
const dOnly = (iso) => (iso || '').slice(0, 10);
// Label a series point by its bucket: quarter for quarterly, year for yearly.
const seriesLabel = (iso, freq) => {
  const d = new Date(`${iso}T00:00:00`);
  if (freq === 'yearly') return String(d.getFullYear());
  if (freq === 'monthly') {
    return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
  }
  return qLabel(iso);
};

// "At Year ended 31 Dec 2024 - Q4 2024" → "YE Dec 24"
const sectionShort = (label) => label
  .replace(/^At\s+/i, '')
  .replace(/\s*-\s*Q\d\s+\d{4}.*$/, '')
  .replace('Year ended ', 'YE ')
  .replace('half year period ', 'HY ')
  .replace('period ended ', '')
  .replace(' (Effective date)', '');

const signColor = (v) => (v > 0 ? GREEN : v < 0 ? RED : 'text.primary');

export default function PpaFairValue() {
  const { portfolioId } = useParams();

  // ── adjustable assumptions ──
  // Valuation-date range: each period renders one FV mark ("quote") to
  // compare. Default = next 5 years, quarterly (the curve's native cadence;
  // the picker snaps this to whatever `granularity` the backend reports).
  const [period, setPeriod] = useState(() => ({
    code: '5y',
    start: dayjs().startOf('month').toISOString(),
    end: dayjs().add(5, 'year').startOf('month').toISOString(),
    frequency: 'quarterly',
    label: '5Y',
  }));
  const [granularity, setGranularity] = useState('quarterly');
  const [basis, setBasis] = useState('baseload');
  const [scenario, setScenario] = useState('central');
  const [discountRate, setDiscountRate] = useState('');   // '' = source default
  const [factor, setFactor] = useState('');               // '' = solved/frozen
  const [nominal, setNominal] = useState(true);
  // 'auto' prefers the uploaded EY workbook; 'model' forces the RevFM
  // Aurora-curve estimate. The toggle only shows once we know a workbook
  // exists (hasWorkbook persists across the switch).
  const [source, setSource] = useState('auto');
  const [hasWorkbook, setHasWorkbook] = useState(false);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // The single-valuation detail anchors at the range end (latest quote);
    // start/end/frequency drive the FV-over-valuation-dates series (model mode).
    const params = {
      valuation_date: dOnly(period.end), basis, scenario,
      start: dOnly(period.start), end: dOnly(period.end),
      frequency: period.frequency,
    };
    if (source === 'model') params.source = 'model';
    if (discountRate !== '') params.discount_rate = discountRate;
    if (factor !== '') params.factor = factor;
    if (!nominal) params.nominal = '0';
    axios.get(`${API}/api/portfolios/${portfolioId}/financial-models/fair-value`,
              { params })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        if (r.data?.granularity) setGranularity(r.data.granularity);
        if (r.data?.source === 'workbook') setHasWorkbook(true);
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.error || e.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [portfolioId, period, basis, scenario, discountRate, factor,
      nominal, source]);

  const isWb = data?.source === 'workbook';
  const fv = isWb ? data.valuation?.fair_value_aud : data?.fair_value_aud;
  const cal = data?.calibration;
  const check = data?.factor_check;

  const periods = useMemo(() => {
    if (isWb) return (data.valuation?.periods || []);
    return (data?.periods || []);
  }, [data, isWb]);
  const remaining = useMemo(
    () => periods.filter((p) => p.pv_aud != null), [periods]);

  // Signed bars need two series (one colour each); 0 — not null — for the
  // inactive sign, nulls poison the d3 stack.
  const chart = useMemo(() => ({
    labels: remaining.map((p) => qLabel(p.period_start)),
    pos: remaining.map((p) => (p.pv_aud > 0 ? p.pv_aud : 0)),
    neg: remaining.map((p) => (p.pv_aud < 0 ? p.pv_aud : 0)),
  }), [remaining]);

  const legChart = useMemo(() => ({
    labels: remaining.map((p) => qLabel(p.period_start)),
    a: remaining.map((p) => (isWb ? p.fixed_aud : p.strike_aud_mwh)),
    b: remaining.map((p) => (isWb ? p.merchant_aud : p.calibrated_price_aud_mwh)),
    c: isWb ? null : remaining.map((p) => p.forecast_price_aud_mwh),
  }), [remaining, isWb]);

  const history = useMemo(() => (isWb ? (data.sections || []) : []), [data, isWb]);

  // Year-scale quick-picks for the valuation-date range (the day-scale MTD/7D
  // defaults are meaningless for a multi-year PPA fair value). "PPA term"
  // appears once the backend has reported the delivery window.
  const fvPresets = useMemo(() => {
    const list = [
      { value: '3y', label: '3Y', range: (now) => ({ start: now, end: now.add(3, 'year') }) },
      { value: '5y', label: '5Y', range: (now) => ({ start: now, end: now.add(5, 'year') }) },
      { value: '10y', label: '10Y', range: (now) => ({ start: now, end: now.add(10, 'year') }) },
    ];
    if (data?.ppa_window?.start && data?.ppa_window?.end) {
      list.unshift({ value: 'ppa', label: 'PPA term',
        range: () => ({ start: dayjs(data.ppa_window.start),
                        end: dayjs(data.ppa_window.end) }) });
    }
    return list;
  }, [data]);

  // FV mark per valuation date ("quotes" to compare). Signed → two colours.
  const seriesData = useMemo(() => {
    const rows = (!isWb && data?.series) ? data.series : [];
    const freq = data?.series_frequency || period.frequency;
    return {
      labels: rows.map((s) => seriesLabel(s.valuation_date, freq)),
      pos: rows.map((s) => (s.fair_value_aud > 0 ? s.fair_value_aud : 0)),
      neg: rows.map((s) => (s.fair_value_aud < 0 ? s.fair_value_aud : 0)),
      n: rows.length,
    };
  }, [data, isWb, period.frequency]);

  return (
    <Box>
      <PpaWorkspaceTabs />
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>PPA Fair Value</Typography>
        {isWb ? (
          <Typography variant="body2" color="text.secondary">
            EY embedded-derivative workbook · {data.workbook?.filename}
          </Typography>
        ) : (data && (
          <Typography variant="body2" color="text.secondary">
            {data.duid} · PPA {data.ppa_window?.start} → {data.ppa_window?.end} ·
            seller perspective
          </Typography>
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isWb
          ? 'Auditor methodology replicated from the uploaded workbook: bundled tariff split into an implied fixed black leg, merchant leg = Aurora Segment-1 × day-1 calibration factor, discounted at the day-1 anchor (EY convention — no re-anchoring, no roll-off).'
          : 'Aurora forecast × day-1 calibration factor vs the contracted tariff, discounted over the remaining delivery quarters.'}
      </Typography>

      {/* ── assumptions bar ── */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap
                 alignItems="center">
            {hasWorkbook && (
              <Tooltip title="EY workbook = the uploaded auditor calc, replicated verbatim. Model estimate = mark-to-model from the RevFM lender model's Aurora curve (scenario/basis selectable).">
                <ToggleButtonGroup
                  size="small" exclusive
                  value={source === 'model' ? 'model' : 'workbook'}
                  onChange={(_, v) => v && setSource(v === 'model' ? 'model' : 'auto')}
                  sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5,
                        textTransform: 'none', fontWeight: 600 } }}>
                  <ToggleButton value="workbook">EY workbook</ToggleButton>
                  <ToggleButton value="model">Model estimate</ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            )}
            {!isWb && (
              <PeriodPicker
                value={period}
                onChange={setPeriod}
                granularity={granularity}
                availableFrequencies={data?.available_frequencies
                  || ['monthly', 'quarterly', 'yearly']}
                presets={fvPresets}
                size="small" />
            )}
            {!isWb && (
              <Select size="small" value={basis}
                      onChange={(e) => setBasis(e.target.value)} sx={{ width: 200 }}>
                <MenuItem value="baseload">Baseload (time-weighted)</MenuItem>
                <MenuItem value="captured">Captured (dispatch-weighted)</MenuItem>
              </Select>
            )}
            {!isWb && (
              <Select size="small" value={scenario}
                      onChange={(e) => setScenario(e.target.value)} sx={{ width: 200 }}>
                <MenuItem value="central">Aurora Central Plus</MenuItem>
                <MenuItem value="messy">Aurora Messy Transition Plus</MenuItem>
              </Select>
            )}
            <TextField
              label={isWb ? 'Discount rate (4.5% default)' : 'Discount % p.a.'}
              size="small" sx={{ width: 190 }}
              value={discountRate}
              placeholder={isWb ? '4.5' : (data ? String(data.assumptions?.discount_rate_pct) : '')}
              onChange={(e) => setDiscountRate(e.target.value.replace(/[^0-9.]/g, ''))} />
            <Tooltip title={isWb
              ? "Leave blank for the workbook's goal-seeked 0.8108. Enter a value to test sensitivity."
              : 'Leave blank to solve the factor at the effective date (zero day-1 NPV).'}>
              <TextField
                label="Factor override" size="small" sx={{ width: 150 }}
                value={factor}
                placeholder={isWb ? (check ? String(check.stated_factor?.toFixed(4)) : '')
                                  : (cal ? String(cal.solved_factor) : '')}
                onChange={(e) => setFactor(e.target.value.replace(/[^0-9.]/g, ''))} />
            </Tooltip>
            {!isWb && (
              <FormControlLabel
                control={<Switch size="small" checked={nominal}
                                 onChange={(e) => setNominal(e.target.checked)} />}
                label={<Typography variant="body2">CPI-nominal</Typography>} />
            )}
            {isWb && (
              <Chip size="small" variant="outlined"
                    label={`Curve vintage: Aurora ${data.valuation?.vintage}`} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {data && (
        <>
          {/* ── headline cards ── */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Card variant="outlined" sx={{ minWidth: 220, flex: 1 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Fair value{isWb
                    ? ` · Aurora ${data.valuation?.vintage} curve`
                    : ` · ${data.valuation_date}`}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: signColor(fv) }}>
                  {fmtAud(fv)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fv >= 0 ? 'asset' : 'liability'} to the project
                  {isWb
                    ? ' · moves with curve vintage (EY: no roll-off)'
                    : ` · ${remaining.length} quarters remaining`}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ minWidth: 220, flex: 1 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Calibration factor
                </Typography>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {isWb
                      ? (check ? `${(check.stated_factor * 100).toFixed(2)}%` : '—')
                      : (cal ? `${(cal.factor * 100).toFixed(1)}%` : '—')}
                  </Typography>
                  {isWb ? (
                    check && Math.abs(check.solved_factor - check.stated_factor) < 1e-4
                      ? <Chip size="small" color="success" variant="outlined"
                              label="goal-seek replicated" />
                      : <Chip size="small" label="check diff" />
                  ) : (cal?.is_override
                    ? <Chip size="small" label="override" />
                    : <Chip size="small" label="solved at day 1" variant="outlined" />)}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {isWb
                    ? `re-solved independently: ${(check?.solved_factor * 100).toFixed(2)}% · zero NPV at Q1 2024 effective date`
                    : `day-1 NPV ${fmtAud(cal?.day1_npv_aud)} at effective ${data.effective_date}`}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ minWidth: 220, flex: 1 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {isWb ? 'Methodology' : 'Merchant curve'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {isWb ? 'EY embedded derivative (IFRS 13)'
                        : data.assumptions?.curve?.scenario}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isWb
                    ? `implied fixed black leg vs Aurora Segment-1 · discount ${(data.valuation?.discount_rate * 100).toFixed(2)}% p.a. anchored at day 1`
                    : `${data.assumptions?.curve?.metric} · ${data.assumptions?.curve?.currency}${data.assumptions?.nominal ? ' → CPI-nominal' : ' (real)'} · discount ${data.assumptions?.discount_rate_pct}% p.a.`}
                </Typography>
              </CardContent>
            </Card>
          </Stack>

          {/* ── fair value across valuation dates (the "quotes") ── */}
          {!isWb && seriesData.n > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Fair value across valuation dates
                  {` · ${data.series_frequency || period.frequency}`}
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  One mark per {data.series_frequency || period.frequency} valuation
                  date over the selected range — how the mark-to-model FV evolves
                  as delivery quarters roll off.
                </Typography>
                <BarChart
                  height={260}
                  xAxis={[{ scaleType: 'band', data: seriesData.labels }]}
                  yAxis={[{ valueFormatter: fmtAud, width: 76 }]}
                  series={[
                    { data: seriesData.pos, label: 'FV (asset)', color: GREEN,
                      stack: 'fv', valueFormatter: fmtAud },
                    { data: seriesData.neg, label: 'FV (liability)', color: RED,
                      stack: 'fv', valueFormatter: fmtAud },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                  margin={{ right: 16, top: 12, bottom: 24 }} />
              </CardContent>
            </Card>
          )}

          {/* ── workbook valuation history ── */}
          {isWb && history.length > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Measurement-date valuations (workbook, replicated to the cent)
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {history.map((s) => (
                    <Box key={s.label} sx={{ minWidth: 150 }}>
                      <Typography variant="caption" color="text.secondary">
                        {sectionShort(s.label)} · Aurora {s.vintage}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700,
                                                     color: signColor(s.fair_value_aud) }}>
                        {fmtAud(s.fair_value_aud)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.recompute_diff_aud != null && Math.abs(s.recompute_diff_aud) < 1
                          ? '✓ matches workbook'
                          : `Δ ${fmtAud(s.recompute_diff_aud)} vs stated`}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── PV per quarter ── */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                PV of net settlement by quarter
                {isWb ? ' (day-1 anchor, latest curve)' : ' (remaining term)'}
              </Typography>
              {remaining.length ? (
                <BarChart
                  height={260}
                  xAxis={[{ scaleType: 'band', data: chart.labels }]}
                  yAxis={[{ valueFormatter: fmtAud, width: 76 }]}
                  series={[
                    { data: chart.pos, label: 'PV (receivable)', color: GREEN,
                      stack: 'pv', valueFormatter: fmtAud },
                    { data: chart.neg, label: 'PV (payable)', color: RED,
                      stack: 'pv', valueFormatter: fmtAud },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                  margin={{ right: 16, top: 12, bottom: 24 }} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No discounted quarters.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* ── legs ── */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {isWb
                  ? 'Fixed (implied black) vs merchant leg by quarter (A$)'
                  : 'Contracted tariff vs calibrated merchant forecast (A$/MWh)'}
              </Typography>
              {remaining.length > 1 && (
                <LineChart
                  height={240}
                  xAxis={[{ scaleType: 'point', data: legChart.labels }]}
                  yAxis={[{ valueFormatter: isWb ? fmtAud : fmtPrice, width: 76 }]}
                  series={[
                    { data: legChart.a,
                      label: isWb ? 'Fixed leg (frozen at day 1)' : 'PPA tariff',
                      color: GREY, showMark: false,
                      valueFormatter: isWb ? fmtAud : fmtPrice },
                    { data: legChart.b,
                      label: isWb ? 'Merchant leg (calibrated)' : 'Calibrated forecast',
                      color: GREEN, showMark: false,
                      valueFormatter: isWb ? fmtAud : fmtPrice },
                    ...(legChart.c ? [{ data: legChart.c,
                      label: 'Aurora forecast (uncalibrated)',
                      color: 'rgba(51,65,85,0.35)', showMark: false,
                      valueFormatter: fmtPrice }] : []),
                  ]}
                  margin={{ right: 16, top: 24, bottom: 24 }} />
              )}
            </CardContent>
          </Card>

          {/* ── per-quarter table ── */}
          <Button
            size="small"
            onClick={() => setShowTable((s) => !s)}
            endIcon={showTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
            {showTable ? 'Hide' : 'Show'} settlement detail
            ({periods.length} quarters)
          </Button>
          <Collapse in={showTable}>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quarter</TableCell>
                    {isWb ? (
                      <>
                        <TableCell align="right">Fixed leg</TableCell>
                        <TableCell align="right">Merchant leg</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">Contracted MWh</TableCell>
                        <TableCell align="right">Tariff</TableCell>
                        <TableCell align="right">Aurora</TableCell>
                        <TableCell align="right">Calibrated</TableCell>
                      </>
                    )}
                    <TableCell align="right">Net settlement</TableCell>
                    <TableCell align="right">DF</TableCell>
                    <TableCell align="right">PV</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map((p) => (
                    <TableRow key={p.period_start}
                              sx={p.pv_aud == null ? { opacity: 0.45 } : undefined}>
                      <TableCell>{qLabel(p.period_start)}</TableCell>
                      {isWb ? (
                        <>
                          <TableCell align="right">{fmtAud(p.fixed_aud)}</TableCell>
                          <TableCell align="right">{fmtAud(p.merchant_aud)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right">{fmtMwh(p.volume_mwh)}</TableCell>
                          <TableCell align="right">{fmtPrice(p.strike_aud_mwh)}</TableCell>
                          <TableCell align="right">{fmtPrice(p.forecast_price_aud_mwh)}</TableCell>
                          <TableCell align="right">{fmtPrice(p.calibrated_price_aud_mwh)}</TableCell>
                        </>
                      )}
                      <TableCell align="right"
                                 sx={{ color: signColor(p.net_settlement_aud) }}>
                        {fmtAud(p.net_settlement_aud)}
                      </TableCell>
                      <TableCell align="right">
                        {p.discount_factor != null ? p.discount_factor.toFixed(4) : '—'}
                      </TableCell>
                      <TableCell align="right"
                                 sx={{ color: signColor(p.pv_aud), fontWeight: 600 }}>
                        {fmtAud(p.pv_aud)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Collapse>
        </>
      )}
    </Box>
  );
}
